import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Badge, Button, Col, Container, Row, Table } from 'reactstrap';
import { faFolderOpen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { SessionContext } from '../../shared/session';
import Header from '../../shared/Header';
import Spinner from '../../shared/Spinner';
import { listOperaciones, deleteOperacion } from '../api/pldApi';

function folioExpediente(operacion) {
    const num = operacion && operacion.num_expediente;
    if (num != null) return `EXP-${String(num).padStart(4, '0')}`;
    const s = String((operacion && (operacion.id || operacion._id)) || '');
    return `EXP-${s.slice(0, 8).toUpperCase()}`;
}

export default function TableroExpedientes() {
    const { unidad } = useParams();
    const history = useHistory();
    const session = useContext(SessionContext);

    const [operaciones, setOperaciones] = useState([]);
    const [isWorking, setIsWorking] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const esInmobiliaria = session.unidad?.actividad === 'inmobiliaria';

    useEffect(() => {
        if (!unidad) return undefined;

        let cancelled = false;

        async function cargar() {
            setIsWorking(true);
            setLoadError(null);
            try {
                const data = await listOperaciones(unidad);
                if (!cancelled) {
                    setOperaciones(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                if (!cancelled) {
                    setLoadError('No se pudieron cargar los expedientes.');
                    setOperaciones([]);
                }
            } finally {
                if (!cancelled) setIsWorking(false);
            }
        }

        cargar();
        return () => {
            cancelled = true;
        };
    }, [unidad]);

    const puedeNuevoTramite =
        session?.permisos?.admin || session?.permisos?.['expedientesPLD'] === 'write';

    async function handleEliminar(exp) {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Eliminar expediente',
            html: `¿Confirma eliminar el expediente <strong>${folioExpediente(exp)}</strong>? Esta acción no se puede deshacer.`,
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545',
        });
        if (!result.isConfirmed) return;
        try {
            await deleteOperacion(unidad, exp.id);
            setOperaciones((prev) => prev.filter((o) => o.id !== exp.id));
        } catch (e) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    (e && e.response && e.response.data && e.response.data.error) ||
                    e.message ||
                    'No se pudo eliminar el expediente.',
            });
        }
    }

    return (
        <Container>
            <Header>Expedientes Abiertos</Header>
            {puedeNuevoTramite ? (
                <Row className="mb-3">
                    <Col>
                        <Button
                            color="primary"
                            type="button"
                            onClick={() => history.push(`/${unidad}/pld/nuevo`)}
                        >
                            Nuevo Trámite
                        </Button>
                    </Col>
                </Row>
            ) : null}
            <Spinner isWorking={isWorking} />
            {loadError ? (
                <p className="text-danger mb-2">{loadError}</p>
            ) : null}
            <Row>
                <Col>
                    <Table responsive bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>Folio (ID)</th>
                                <th>Expediente / Operación</th>
                                <th>Fecha de Apertura</th>
                                <th>Estado</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isWorking && operaciones.length === 0 && !loadError ? (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        No hay expedientes guardados. Crea uno nuevo.
                                    </td>
                                </tr>
                            ) : null}
                            {operaciones.map((exp) => (
                                <tr key={exp.id}>
                                    <td className="font-weight-bold text-primary text-monospace">
                                        {folioExpediente(exp)}
                                    </td>
                                    <td>
                                        <div className="font-weight-bold text-uppercase">{esInmobiliaria ? exp.asunto : exp.denominacion}</div>
                                        <div className="small text-muted">{exp.tipo_operacion}</div>
                                    </td>
                                    <td>
                                        {exp.fecha_apertura
                                            ? new Date(exp.fecha_apertura).toLocaleDateString()
                                            : ''}
                                    </td>
                                    <td>
                                        <Badge
                                            color={exp.estado === 'En Integración' ? 'success' : 'warning'}
                                            pill
                                            className="text-uppercase"
                                        >
                                            {exp.estado || 'Faltan Socios'}
                                        </Badge>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            type="button"
                                            onClick={() =>
                                                history.push(`/${unidad}/pld/${exp.id}`)
                                            }
                                        >
                                            <FontAwesomeIcon icon={faFolderOpen} className="mr-1" />
                                            ABRIR
                                        </Button>
                                        {puedeNuevoTramite ? (
                                            <Button
                                                color="danger"
                                                outline
                                                size="sm"
                                                type="button"
                                                className="ml-2"
                                                onClick={() => handleEliminar(exp)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                                                ELIMINAR
                                            </Button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );
}
